import { PageHeader } from "@justworkshr/milo-components";
import React, { useState } from "react";
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import {
  CENSUS_TITLE,
  BACK_TO_APP_HOME,
} from "pages/CensusAndCurrentInformation/utils/constants";
import styles from "./CensusAndCurrentInformation.module.css";
import Error from "uicomponents/Error";
import Alert from "uicomponents/Alert";
import { trackFormError } from "utils/analytics/trackEvent";
import { mapFEStepstoMatchBE } from "./utils/helper";
import { HealthAppStepType } from "types";
import { getCompanyId } from "utils/idHelpers";
import { getAppType, getStepNumberFromURL } from "utils/helper";

type ContextType = {
  nextStep: () => void;
  previousStep: () => void;
  stepperNextStep: (stepNumber: number) => void;
};

interface IBusinessAttributesProps {
  data: HealthAppStepType;
}
export const useCensusAndCurrentInformationContext = () => {
  return useOutletContext<ContextType>();
};

const CensusAndCurrentInformation = ({
  ...props
}: IBusinessAttributesProps): JSX.Element => {
  const { data } = props;
  const healthAppUrl =
    getAppType() === "HEALTH_INSURANCE"
      ? "/health-insurance-application"
      : "/health-insurance-application-mdv";

  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const companyUuid = getCompanyId();

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;

  const [stepError, setStepError] = useState(false);

  if (!data?.applications?.[0]) {
    return <Error />;
  }

  const nextStep = async () => {
    const currentStep = getStepNumberFromURL();
    const lastStepWithoutCobra = !hasCobra && currentStep === 6;
    try {
      const lastStep = currentStep >= 7;
      if (lastStep || lastStepWithoutCobra) {
        navigate(`${healthAppUrl}/${companyUuid}`);
        return;
      }
      navigate({
        pathname: `${currentStep + 1}`,
        search: `?${searchParams.toString()}`,
      });
    } catch (error: any) {
      setStepError(true);
      trackFormError(
        error,
        `error updating census and current information form step- ${error?.message}`
      );
      return;
    }
  };

  const previousStep = (): void => {
    const currentStep = getStepNumberFromURL();
    const isLastStep = getStepNumberFromURL() === 5;
    if (isLastStep) {
      const companyUuid = getCompanyId();
      navigate(`${healthAppUrl}/${companyUuid}`);
      return;
    }
    navigate({
      pathname: `${currentStep - 1}`,
      search: `?${searchParams.toString()}`,
    });
  };

  const stepperNextStep = (stepNumber: number) => {
    const newStep = mapFEStepstoMatchBE(stepNumber);
    navigate({
      pathname: `${newStep}`,
      search: `?${searchParams.toString()}`,
    });
  };

  return (
    <div className={styles.censusAndCurrentInformationContainer}>
      <div className={styles.titleContainer}>
        <PageHeader
          className={styles.censusAndCurrentInformationpageHeader}
          title={CENSUS_TITLE}
          linkPrevious={
            <a href={`${healthAppUrl}/${companyUuid}`}>{BACK_TO_APP_HOME}</a>
          }
        />
      </div>
      <>
        <Outlet
          context={{ previousStep, nextStep, stepperNextStep } as ContextType}
        />
        {stepError && <Alert />}
      </>
    </div>
  );
};
export default CensusAndCurrentInformation;
